/**
 * BuildArtifactsView — container for the Build & Artifacts mainView.
 *
 * Renders PhaseStepper at top, then phase-specific content:
 * - 'requirements' → embeds full research workspace (same components, same store)
 * - 'build' → ProcessViewer (DAG / Tool)
 * - others → disabled placeholders
 */

import React, { useState, useMemo, useRef } from 'react';
import PhaseStepper from './PhaseStepper';
import ProcessViewer from './ProcessViewer';
import PlanPanel from './PlanPanel';
import PlanGraphView, { PlanNodeData, PlanEdgeData } from './PlanGraphView';
import InstitutionProfilePanel from './InstitutionProfilePanel';
import OutputPackageView from './OutputPackageView';
import DocumentationView from './DocumentationView';
import GeneratedDocumentsView from './GeneratedDocumentsView';
import ModelResultsView from './ModelResultsView';
import type { BuildPhase } from '../../types/modeling';
import type { ReportDocument } from '../../apiService';
import { useProjectStore } from '../../stores/projectStore';
import { ResearchWorkspace, WorkspaceTabs } from '../workspace';
// OnlineSupportPanel / OnlineSupportToggleButton no longer mounted —
// online support renders inside UnifiedFindingsPanel as a synthetic
// document group (BAR-39 UI consolidation).
// PlanTrainTrack moved to project header (App.tsx)
import ReportView from '../workspace/ReportView';
import UnifiedFindingsPanel, { FindingsToggleButton } from '../workspace/UnifiedFindingsPanel';
import type { WorkspaceMode } from '../../types/workspace';
import { getGoogleWorkspaceEmail } from '../../utils/googleWorkspace';
import { computeIsWelcomeState } from '../../utils/welcomeState';
import { dictToPlanGraph } from '../../utils/planGraphData';
import WorkspaceBrief from '../workspace/WorkspaceBrief';
interface BuildArtifactsViewProps {
  /** Nullable to support Build-page draft mode, where the user has pressed
   *  a topic box but no project has been created yet. In draft mode the
   *  view renders the plan-phase canvas (which doesn't need a real project
   *  id) and any phase that does require one (e.g. build) renders a
   *  placeholder rather than firing an API call with junk. */
  projectId: string | null;
  authToken: string;
  userId?: string;
  linkedReport: ReportDocument | null;
  isAgentActive?: boolean;
  onValidateClick?: () => void;
  /** BAR-192 Phase E — dispatches a pre-filled instruction into the
   *  workspace chat (the report Fix button). Absent → Fix hidden. */
  onDispatchChatMessage?: (text: string) => void;
}

// Map workspace mode ↔ build phase (single source of truth)
const MODE_TO_PHASE: Record<WorkspaceMode, BuildPhase> = {
  plan: 'plan',
  institution_context: 'institution_context',
  research: 'requirements',
  production: 'build',
  documentation: 'documentation',
  output_package: 'output_package',
};
const PHASE_TO_MODE: Record<string, WorkspaceMode> = {
  plan: 'plan',
  institution_context: 'institution_context',
  requirements: 'research',
  build: 'production',
  documentation: 'documentation',
  output_package: 'output_package',
};

export default function BuildArtifactsView({
  projectId,
  authToken,
  userId,
  linkedReport,
  isAgentActive = false,
  onValidateClick,
  onDispatchChatMessage,
}: BuildArtifactsViewProps) {
  const { workspaceMode, dictWorkspace, plan, setWorkspaceMode, loadSpecificWorkspace } = useProjectStore();
  const googleWorkspaceEmail = useMemo(() => userId ? getGoogleWorkspaceEmail(userId) : null, [userId]);

  // Build phase sub-view: the DAG editor vs the "Generated documents" review
  // home (ship-gate Step 2d). The model-derived docs (doc_kind="model_derived")
  // are produced in the production workspace on model acceptance (Slice 2c).
  const [buildSubView, setBuildSubView] = useState<'dag' | 'docs' | 'results'>('dag');
  const generatedDocCount = useMemo(
    () =>
      Object.values(dictWorkspace?.doc_files ?? {}).filter(
        (f) => f.doc_kind === 'model_derived',
      ).length,
    [dictWorkspace?.doc_files],
  );
  const hasDashboard = useMemo(
    () =>
      Object.values(dictWorkspace?.doc_files ?? {}).some(
        (f) => f.doc_kind === 'dashboard_html',
      ),
    [dictWorkspace?.doc_files],
  );

  // Derive active phase from store's workspaceMode (user's viewport)
  // Phase stepper is now rendered in the unified project header (App.tsx)
  const activePhase = MODE_TO_PHASE[workspaceMode] ?? 'requirements';
  const planWorkspaceId = projectId ? `ws_${projectId}` : null;
  const isPlanWorkspaceCurrent = !!(
    planWorkspaceId &&
    dictWorkspace?.workspace_id === planWorkspaceId
  );
  const hasPlanNodes = !!(
    isPlanWorkspaceCurrent &&
    Object.keys(dictWorkspace!.nodes || {}).length > 0
  );

  // Map the plan workspace into PlanGraphView's grid model (columns =
  // workspace_type, lanes = clusters). Frontend-only view transform.
  const planGraph = useMemo(
    () => (hasPlanNodes && dictWorkspace
      ? dictToPlanGraph(dictWorkspace)
      : { nodes: [] as PlanNodeData[], edges: [] as PlanEdgeData[] }),
    [hasPlanNodes, dictWorkspace],
  );

  return (
    <div className="h-full flex flex-col bg-bg-warm">
      {/* Plan train track moved to project header in App.tsx */}
      {/* Workspace brief — the plan node's what/inputs/output presented
          exactly as in the plan view, docked top + collapsible. Self-gating:
          null unless a plan node matches the current workspace (so the plan
          phase itself never shows it). */}
      <WorkspaceBrief workspaceId={dictWorkspace?.workspace_id} />
      {/* Phase content (stepper moved to project header) */}
      <div className="flex-1 overflow-hidden">
        <PhaseErrorBoundary>
          {activePhase === 'plan' ? (
            /* Plan graph renders as a 2-D enterprise grid (PlanGraphView): the
               objective header, columns = workspace_type phase, Step lanes, and
               the bottom detail drawer all live inside PlanGraphView. */
            hasPlanNodes ? (
              <PlanGraphView
                key={`plan-${dictWorkspace!.workspace_id}`}
                nodes={planGraph.nodes}
                edges={planGraph.edges}
                clusters={dictWorkspace?.clusters || {}}
                objective={plan?.objective || (dictWorkspace as any)?.plan_objective || ''}
                onNodeClick={async (nodeId) => {
                  // Double-click a plan node → open its workspace. Await
                  // loadSpecificWorkspace before switching mode — see
                  // PlanTrainTrack.drillIntoNode for the full rationale
                  // (same race / silent-failure trap).
                  const node = dictWorkspace?.nodes?.[nodeId] as any;
                  if (!node) return;
                  const wsType = node.workspace_type;
                  const wsId = node.workspace_id;
                  if (!wsId || !userId || !wsType) return;

                  const loaded = await loadSpecificWorkspace(wsId, userId);
                  if (!loaded) {
                    console.warn(
                      '[BuildArtifactsView] loadSpecificWorkspace returned false for',
                      wsId,
                      '— aborting navigation rather than leaving the user on a stale view.'
                    );
                    return;
                  }

                  if (wsType === 'research') setWorkspaceMode('research', 'user');
                  else if (wsType === 'production') setWorkspaceMode('production', 'user');
                  else if (wsType === 'institution_context') setWorkspaceMode('institution_context', 'user');
                  else if (wsType === 'documentation') setWorkspaceMode('documentation', 'user');
                  else if (wsType === 'output_package') setWorkspaceMode('output_package', 'user');
                }}
              />
            ) : isPlanWorkspaceCurrent || !projectId ? (
              <PlanGraphView nodes={[]} edges={[]} objective={plan?.objective || ''} />
            ) : (
              <PlanLoadingPlaceholder />
            )
          ) : activePhase === 'institution_context' ? (
            <InstitutionProfilePanel />
          ) : activePhase === 'requirements' ? (
            <RequirementsPhase linkedReport={linkedReport} userId={userId} authToken={authToken} onValidateClick={onValidateClick} onDispatchChatMessage={onDispatchChatMessage} />
          ) : activePhase === 'build' ? (
            // Guard against draft mode reaching the build phase: ProcessViewer
            // requires a real projectId for its API calls. Draft mode never
            // sets workspaceMode='build' so this is defensive — but the type
            // system enforces the guard rather than relying on runtime invariant.
            projectId ? (
              <div className="h-full flex flex-col">
                {/* Build sub-tabs: the DAG editor vs the Generated-documents
                    review home (ship-gate Step 2d). */}
                <div className="flex items-center gap-1 px-3 h-10 flex-shrink-0 border-b border-border-default bg-surface">
                  <button
                    type="button"
                    onClick={() => setBuildSubView('dag')}
                    className={`inline-flex items-center h-7 px-3 rounded-md text-xs font-semibold transition-colors ${
                      buildSubView === 'dag'
                        ? 'bg-primary-tint text-fg'
                        : 'text-fg-secondary hover:bg-bg-warm'
                    }`}
                  >
                    Build
                  </button>
                  <button
                    type="button"
                    onClick={() => setBuildSubView('docs')}
                    className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-md text-xs font-semibold transition-colors ${
                      buildSubView === 'docs'
                        ? 'bg-primary-tint text-fg'
                        : 'text-fg-secondary hover:bg-bg-warm'
                    }`}
                  >
                    Generated documents
                    {generatedDocCount > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold bg-surface-sunken text-fg-secondary border border-border-default">
                        {generatedDocCount}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setBuildSubView('results')}
                    className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-md text-xs font-semibold transition-colors ${
                      buildSubView === 'results'
                        ? 'bg-primary-tint text-fg'
                        : 'text-fg-secondary hover:bg-bg-warm'
                    }`}
                  >
                    Results
                    {hasDashboard && (
                      <span className="inline-flex w-1.5 h-1.5 rounded-full bg-success" aria-hidden />
                    )}
                  </button>
                </div>
                <div className="flex-1 min-h-0 overflow-hidden">
                  {buildSubView === 'docs' ? (
                    <GeneratedDocumentsView
                      workspaceId={dictWorkspace?.workspace_id ?? null}
                      authToken={authToken}
                      userId={userId}
                    />
                  ) : buildSubView === 'results' ? (
                    <ModelResultsView
                      projectId={projectId}
                      authToken={authToken}
                      userId={userId}
                    />
                  ) : (
                    <ProcessViewer
                      projectId={projectId}
                      workspaceId={dictWorkspace?.workspace_id ?? null}
                      authToken={authToken}
                      userId={userId}
                      googleWorkspaceEmail={googleWorkspaceEmail}
                    />
                  )}
                </div>
              </div>
            ) : (
              <DisabledPhasePlaceholder phase={activePhase} />
            )
          ) : activePhase === 'documentation' ? (
            <DocumentationView
              projectId={projectId}
              workspaceId={dictWorkspace?.workspace_id ?? null}
              authToken={authToken}
              userId={userId}
            />
          ) : activePhase === 'output_package' ? (
            <OutputPackageView
              projectId={projectId}
              workspaceId={dictWorkspace?.workspace_id ?? null}
              authToken={authToken}
              userId={userId}
            />
          ) : (
            <DisabledPhasePlaceholder phase={activePhase} />
          )}
        </PhaseErrorBoundary>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Requirements phase — embeds the full research workspace
// ---------------------------------------------------------------------------

function RequirementsPhase({ linkedReport, userId, authToken, onValidateClick, onDispatchChatMessage }: { linkedReport: ReportDocument | null; userId?: string; authToken?: string; onValidateClick?: () => void; onDispatchChatMessage?: (text: string) => void }) {
  const {
    activeProject,
    dictWorkspace,
    activeWorkspace,
    setActiveWorkspace,
    pendingWorkspaceSwitch,
    acceptWorkspaceSwitch,
    rejectWorkspaceSwitch,
    updateWorkspace,
  } = useProjectStore();

  const [highlightedNodeId, setHighlightedNodeId] = useState<string | null>(null);
  const [isSourcePanelOpen, setIsSourcePanelOpen] = useState(false);
  const [sourcePanelContent, setSourcePanelContent] = useState<any>(null);
  const [reLayoutTrigger, setReLayoutTrigger] = useState(0);

  // Report findings panel state
  const [isReportFindingsPanelOpen, setIsReportFindingsPanelOpen] = useState(false);
  const [selectedReportFindingId, setSelectedReportFindingId] = useState<string | null>(null);
  const [selectedReportOnlineSupportId, setSelectedReportOnlineSupportId] = useState<string | null>(null);
  const [isReportDetailOpen, setIsReportDetailOpen] = useState(false);
  // Tracks the previous detail-open state so we only clear the report
  // selection on a genuine open→closed transition — not the transient
  // `false` the findings panel emits on first open (see App.tsx).
  const prevReportDetailOpenRef = useRef(false);
  const [reportSplitPercent, setReportSplitPercent] = useState(50);
  const [reportListWidth, setReportListWidth] = useState(220);
  // OnlineSupport panel state removed — online support is now a
  // synthetic group inside UnifiedFindingsPanel.

  if (!activeProject) {
    return (
      <div className="h-full flex items-center justify-center text-fg-tertiary">
        No active project
      </div>
    );
  }

  // Welcome-to-Research empty state — disables the Report/Graph tab
  // pill and lets ReportView / ResearchWorkspace render the walkthrough.
  // Heuristic lives in utils/welcomeState.ts so this view and the
  // App.tsx workspace path stay in lock-step.
  const isWelcomeState = computeIsWelcomeState(dictWorkspace, activeProject);

  // Defensive guard: detect a stale dictWorkspace that still points at
  // the plan workspace while the user has navigated into a sub-
  // workspace. This happens during the brief window between
  // setWorkspaceMode() flipping us into the 'requirements' phase and
  // loadSpecificWorkspace() updating dictWorkspace to the new
  // workspace. Without this guard, ResearchWorkspace renders the plan
  // workspace's logic_nodes as if they were research nodes -- exactly
  // matching the plan-graph look the user sees, just plotted by the
  // wrong renderer. The unawaited race in PlanTrainTrack.drillIntoNode
  // and BuildArtifactsView.onNodeDoubleClick is the root cause; this
  // is the belt to the suspenders so a future regression on those
  // handlers doesn't reintroduce the visual confusion.
  const planWorkspaceId = activeProject.project_id
    ? `ws_${activeProject.project_id}`
    : null;
  const isStalePlanData = !!(
    planWorkspaceId
    && dictWorkspace?.workspace_id === planWorkspaceId
  );
  const safeGraph = isStalePlanData ? undefined : (dictWorkspace ?? undefined);
  const safeFindings = isStalePlanData
    ? activeProject.findings
    : (dictWorkspace?.findings || activeProject.findings);
  const safeMarkers = isStalePlanData ? {} : (dictWorkspace?.markers || {});

  const workspaceTabsEl = (
    <WorkspaceTabs
      activeWorkspace={activeWorkspace}
      onWorkspaceChange={setActiveWorkspace}
      pendingSwitch={pendingWorkspaceSwitch}
      onAcceptSwitch={acceptWorkspaceSwitch}
      onRejectSwitch={rejectWorkspaceSwitch}
      disabled={isWelcomeState}
    />
  );

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-hidden relative">
        {activeWorkspace === 'graph' ? (
          <ResearchWorkspace
            key={`requirements-${safeGraph?.workspace_id || 'empty'}`}
            graph={safeGraph}
            references={linkedReport?.references}
            findings={safeFindings}
            markers={safeMarkers}
            onlineSupport={safeGraph?.online_support || {}}
            workspaceId={safeGraph?.workspace_id}
            compartmentId={safeGraph?.compartment_id || 'default'}
            editable={true}
            initialPositions={activeProject.workspace_ui_state?.node_positions}
            onPositionsChange={(positions: any) => {
              updateWorkspace(activeProject.workspace, {
                ...activeProject.workspace_ui_state,
                node_positions: positions,
              });
            }}
            notes={(activeProject as any).notes || ''}
            onNotesChange={() => {}}
            highlightedNodeId={highlightedNodeId}
            isSourcePanelOpen={isSourcePanelOpen}
            sourcePanelContent={sourcePanelContent}
            onCloseSourcePanel={() => {
              setIsSourcePanelOpen(false);
              setSourcePanelContent(null);
            }}
            onNavigateToNode={(nodeId: string) => {
              setHighlightedNodeId(nodeId);
              setTimeout(() => setHighlightedNodeId(null), 3000);
            }}
            triggerReLayout={reLayoutTrigger}
            onValidateClick={onValidateClick}
            headerSlot={workspaceTabsEl}
            isWelcomeState={isWelcomeState}
          />
        ) : (
          <div className="flex h-full bg-bg-warm">
            <div
              className={`min-w-0 relative ${isReportDetailOpen ? '' : 'flex-1'}`}
              style={isReportDetailOpen ? { width: `${reportSplitPercent}%` } : undefined}
            >
              {workspaceTabsEl}
              {/* Report frame: ReportView renders the framed card and owns
                  its own vertical scroll inside the grey workspace shell. */}
              <div className="h-full">
                <ReportView
                  report={dictWorkspace?.reports ? Object.values(dictWorkspace.reports)[0] || null : null}
                  draftReport={activeProject.draft_report || null}
                  markers={dictWorkspace?.markers || {}}
                  findings={dictWorkspace?.findings || {}}
                  onlineSupport={dictWorkspace?.online_support || {}}
                  selectedFindingId={selectedReportFindingId}
                  selectedOnlineSupportId={selectedReportOnlineSupportId}
                  workspaceId={dictWorkspace?.workspace_id}
                  userId={userId}
                  authToken={authToken}
                  onDispatchChatMessage={onDispatchChatMessage}
                  isWelcomeState={isWelcomeState}
                  onCitationClick={(findingId: string) => {
                    setSelectedReportFindingId(findingId);
                    setSelectedReportOnlineSupportId(null); // cross-type mutual exclusion: only one chip highlighted
                    setIsReportFindingsPanelOpen(true);
                  }}
                  onOnlineRefClick={(onlineSupportId: string) => {
                    setSelectedReportOnlineSupportId(onlineSupportId);
                    setSelectedReportFindingId(null); // cross-type mutual exclusion: only one chip highlighted
                    setIsReportFindingsPanelOpen(true);
                  }}
                />
              </div>
              {(() => {
                const findingsCount = Object.keys(dictWorkspace?.findings || activeProject?.findings || {}).length;
                const onlineSupportCount = Object.keys(dictWorkspace?.online_support || {}).length;
                const uploadedFileCount = Object.keys(dictWorkspace?.uploaded_files || {}).length;
                return !isReportFindingsPanelOpen && (findingsCount + onlineSupportCount + uploadedFileCount > 0) ? (
                  <FindingsToggleButton
                    findingsCount={findingsCount + onlineSupportCount + uploadedFileCount}
                    onToggle={() => setIsReportFindingsPanelOpen(true)}
                  />
                ) : null;
              })()}
            </div>
            <div
              className="h-full flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden"
              style={{
                width: isReportFindingsPanelOpen
                  ? isReportDetailOpen
                    ? `${100 - reportSplitPercent}%`
                    : reportListWidth
                  : 0,
              }}
            >
              <UnifiedFindingsPanel
                allFindings={dictWorkspace?.findings || activeProject.findings || {}}
                allMarkers={dictWorkspace?.markers || {}}
                allOnlineSupport={dictWorkspace?.online_support || {}}
                allUploadedFiles={dictWorkspace?.uploaded_files || {}}
                linkedOutputs={dictWorkspace?.linked_outputs || []}
                selectedFindingId={selectedReportFindingId}
                selectedOnlineSupportId={selectedReportOnlineSupportId}
                isOpen={isReportFindingsPanelOpen}
                workspaceId={dictWorkspace?.workspace_id}
                compartmentId={dictWorkspace?.compartment_id || 'default'}
                onToggle={() => {
                  setIsReportFindingsPanelOpen(!isReportFindingsPanelOpen);
                }}
                onFindingSelect={(id: string) => {
                  setSelectedReportFindingId(id);
                  setSelectedReportOnlineSupportId(null); // cross-type mutual exclusion: picking a finding clears any online highlight
                  setIsReportFindingsPanelOpen(true);
                }}
                onClose={() => {
                  setSelectedReportFindingId(null);
                  setIsReportDetailOpen(false);
                  setIsReportFindingsPanelOpen(false);
                }}
                onNavigateToNode={(nodeId: string) => {
                  setActiveWorkspace('graph');
                  setHighlightedNodeId(nodeId);
                  setTimeout(() => setHighlightedNodeId(null), 3000);
                }}
                onDetailOpenChange={(open) => {
                  setIsReportDetailOpen(open);
                  // Clear the report-driven selection only on a genuine
                  // open→closed transition (props are value-gated; a repeat
                  // click of the same id would be a dead click otherwise).
                  // Guarding on the previous state avoids the transient
                  // `false` the panel emits on first open from wiping the
                  // just-clicked selection and de-highlighting the chip.
                  if (prevReportDetailOpenRef.current && !open) {
                    setSelectedReportFindingId(null);
                    setSelectedReportOnlineSupportId(null);
                  }
                  prevReportDetailOpenRef.current = open;
                }}
                onPanelResize={(delta: number) => {
                  if (isReportDetailOpen) {
                    const parentEl = document.querySelector('.flex.h-full') as HTMLElement;
                    const containerWidth = parentEl?.offsetWidth || window.innerWidth;
                    const pctDelta = (delta / containerWidth) * 100;
                    setReportSplitPercent((p) => Math.max(25, Math.min(75, p + pctDelta)));
                  } else {
                    setReportListWidth((w) => Math.max(160, Math.min(500, w - delta)));
                  }
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Disabled phase placeholder
// ---------------------------------------------------------------------------

function PlanLoadingPlaceholder() {
  return (
    <div className="h-full flex items-center justify-center bg-bg-warm">
      <div className="text-sm text-fg-tertiary">Loading project plan...</div>
    </div>
  );
}

const PHASE_DESCRIPTIONS: Record<string, { title: string; description: string }> = {
  task: {
    title: 'Task definition',
    description: 'Define the modeling task, scope, and objectives. This phase will guide the model construction process.',
  },
  institution_context: {
    title: 'Institution context',
    description: 'Configure institution-specific parameters such as jurisdiction, business model, and regulatory approach.',
  },
  run: {
    title: 'Model execution',
    description: 'Execute models, review results, and validate outputs against regulatory requirements.',
  },
};

function DisabledPhasePlaceholder({ phase }: { phase: BuildPhase }) {
  const info = PHASE_DESCRIPTIONS[phase] || { title: phase, description: '' };

  return (
    <div className="h-full flex items-center justify-center bg-bg-warm">
      <div className="text-center max-w-sm">
        <div className="w-12 h-12 rounded-full bg-bg-warm flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-fg-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-fg-secondary mb-2">{info.title}</h3>
        <p className="text-sm text-fg-tertiary mb-4">{info.description}</p>
        <span className="inline-block px-3 py-1 text-xs font-medium text-fg-tertiary bg-bg-warm rounded-full">
          Coming soon
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error boundary
// ---------------------------------------------------------------------------

class PhaseErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: string | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error: error.message };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-full flex items-center justify-center bg-danger-bg">
          <div className="text-center max-w-sm">
            <h3 className="text-lg font-semibold text-danger mb-2">Something went wrong</h3>
            <p className="text-sm text-danger mb-4">{this.state.error}</p>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="px-4 py-2 text-sm bg-danger text-white rounded-md hover:bg-danger transition-colors"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
